import { CheckCircle2, BookOpen, Users, User } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { WHO_FOR, WA_NUMBER_2, WA_BOOK_MSG, waLink, trackEvent, openPaymentModal } from "@/data/content";

const ClosingSections = () => (
  <>
    {/* Who is IQMIZE for */}
    <section id="who-for" data-testid="who-for-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHeading
            eyebrow="Who is IQMIZE for?"
            title="For learners who want better habits, not longer hours."
            description="IQMIZE is designed primarily for students and learners who want to develop better learning habits. It can be useful for students who:"
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="who-for-grid">
            {WHO_FOR.map((item) => (
              <div key={item} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-brand" />
                <span className="text-sm font-semibold text-slate-800">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-slate-500">
            For students below 18, the IQMIZE Parent's Guide helps parents support the learning process — without
            turning IQMIZE into another homework assignment.
          </p>
        </div>

        <div className="space-y-6 lg:pt-10">
          <Reveal>
            <div data-testid="note-to-students" className="rounded-3xl border-l-4 border-igold bg-brand-light p-8">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-brand">A note to students</p>
              <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900">
                You are not expected to remember everything perfectly on the first try.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Forgetting something does not mean you are a poor learner. It gives you useful information:
                <span className="font-bold text-inavy"> "This is something I need to practice again."</span> Instead
                of becoming frustrated, ask: "What does this tell me about what I need to practice next?"
              </p>
              <p className="mt-4 text-sm font-bold text-slate-800">
                Learn more systematically. Understand more deeply. Remember more effectively.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="philosophy-card" className="rounded-3xl bg-inavy p-8 text-white">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-igold">Our Philosophy</p>
              <h3 className="mt-3 text-2xl font-bold tracking-tight">Small Steps. Big Results.</h3>
              <p className="mt-3 text-sm leading-relaxed text-sky-100/85">
                You can train the way you learn. Learning is a process — the more consciously you practice that
                process, the more familiar it can become.
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em] font-bold text-igold">
                Learn • Think • Grow • Achieve
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Final CTA */}
    <section id="start" data-testid="final-cta-section" className="py-20 lg:py-28 bg-gradient-to-b from-brand-light to-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="Ready to start your IQMIZE journey?"
          title="Choose how you want to begin."
        />

        <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="final-cta-cards">
          {[
            { icon: BookOpen, label: "Download", name: "IQMIZE Digital PDF", price: "₹49", testid: "final-cta-book", highlight: true },
            { icon: Users, label: "Group Mentoring", name: "7-Day Program", price: "₹4,900", testid: "final-cta-group", highlight: false },
            { icon: User, label: "Individual Mentoring", name: "7-Day One-to-One Program", price: "₹19,000", testid: "final-cta-individual", highlight: false },
          ].map((c, i) => (
            <Reveal key={c.testid} delay={i * 0.1}>
              {i === 0 ? (
                <button
                  type="button"
                  data-testid={c.testid}
                  onClick={() => openPaymentModal("final-cta-book")}
                  className="block w-full h-full rounded-3xl p-8 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 bg-brand text-white shadow-[0_20px_40px_rgb(3,105,161,0.25)] hover:shadow-[0_28px_50px_rgb(3,105,161,0.32)]"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl p-3 bg-white/15 text-igold">
                    <c.icon size={26} strokeWidth={1.8} />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-[0.2em] font-bold text-sky-100">{c.label}</p>
                  <h3 className="mt-2 text-lg font-bold tracking-tight text-white">{c.name}</h3>
                  <p className="mt-3 text-3xl font-extrabold text-igold">{c.price}</p>
                </button>
              ) : (
                <a
                  href={waLink(WA_NUMBER_2, WA_BOOK_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  onClick={() => trackEvent("whatsapp_click", { source: c.testid, number: WA_NUMBER_2 })}
                  className="block h-full rounded-3xl p-8 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 border border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.10)]"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl p-3 bg-brand-light text-brand">
                    <c.icon size={26} strokeWidth={1.8} />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-[0.2em] font-bold text-brand">{c.label}</p>
                  <h3 className="mt-2 text-lg font-bold tracking-tight text-slate-900">{c.name}</h3>
                  <p className="mt-3 text-3xl font-extrabold text-slate-900">{c.price}</p>
                </a>
              )}
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-12 text-center">
          <p className="text-sm font-semibold text-slate-500">WhatsApp IQMIZE</p>
          <p className="mt-1 text-lg font-extrabold tracking-wide text-inavy" data-testid="final-cta-whatsapp-numbers">
            70346 18014 &nbsp;|&nbsp; 85905 66268
          </p>
          <p className="mt-6 text-base font-bold text-slate-700">Train Your Mind. Build Better Learning Habits.</p>
        </Reveal>
      </div>
    </section>
  </>
);

export default ClosingSections;
